import { BoxV2 as Box, Tooltip } from 'portal-commons';
import { FunctionComponent } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { faBullhorn } from '@fortawesome/pro-regular-svg-icons';
import { Grid, TextField } from '@material-ui/core';

import HeadingBlock from './HeadingBlock';
import StyledTextField from './StyledTextField';
import { CampaignDetailsFormValues } from '../../types';
import { charCounter, isFieldGreyOut } from '../../utils';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isValidInput,
} from '../../validators';
import { UsecaseType } from '@/utils/types';

interface CampaignDetailsSectionProp {
  useCase: UsecaseType;
  greyOutFields: string[];
}

const CampaignDetailsSection: FunctionComponent<CampaignDetailsSectionProp> = ({
  useCase,
  greyOutFields,
}) => {
  const { control } = useFormContext<CampaignDetailsFormValues>();
  const {
    field: {
      disabled: descriptionDisabled,
      value: description,
      onChange: setDescription,
      onBlur: validateDescription,
    },
    fieldState: { error: descriptionError },
  } = useController({
    control,
    name: 'description',
    disabled: isFieldGreyOut(greyOutFields, 'description'),
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator(
          'Please enter Campaign Description'
        ),
        withValidMinLength: createMinLengthValidator(40),
        withValidMaxLength: createMaxLengthValidator(4096),
      },
    },
  });
  const {
    field: {
      disabled: messageFlowDisabled,
      value: messageFlow,
      onChange: setMessageFlow,
      onBlur: validateMessageFlow,
    },
    fieldState: { error: messageFlowError },
  } = useController({
    control,
    name: 'messageFlow',
    disabled: isFieldGreyOut(greyOutFields, 'messageFlow'),
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator(
          'Please enter Call-to-Action / Message Flow'
        ),
        withValidMinLength: createMinLengthValidator(40),
        withValidMaxLength: createMaxLengthValidator(4096),
      },
    },
  });
  const {
    field: {
      disabled: termsAndConditionsLinkDisabled,
      value: termsAndConditionsLink,
      onChange: setTermsAndConditionsLink,
      onBlur: validateTermsAndConditionsLink,
    },
    fieldState: { error: termsAndConditionsLinkError },
  } = useController({
    control,
    name: 'termsAndConditionsLink',
    disabled: isFieldGreyOut(greyOutFields, 'termsAndConditionsLink'),
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2048),
      },
    },
  });
  const {
    field: {
      disabled: privacyPolicyLinkDisabled,
      value: privacyPolicyLink,
      onChange: setPrivacyPolicyLink,
      onBlur: validatePrivacyPolicyLink,
    },
    fieldState: { error: privacyPolicyLinkError },
  } = useController({
    control,
    name: 'privacyPolicyLink',
    disabled: isFieldGreyOut(greyOutFields, 'privacyPolicyLink'),
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(2048),
      },
    },
  });

  const {
    field: {
      disabled: referenceIdDisabled,
      value: referenceId,
      onChange: setReferenceId,
      onBlur: validateReferenceId,
    },
    fieldState: { error: referenceIdError },
  } = useController({
    control,
    name: 'referenceId',
    disabled: isFieldGreyOut(greyOutFields, 'referenceId'),
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });

  return (
    <Grid item xs={12} data-testid="campaignDetailsFormCampaignDetails">
      <div className="campaign-details wrapper">
        <HeadingBlock icon={faBullhorn} title="Campaign Details" />
        <Box
          as="p"
          sx={{
            fontSize: 'H300',
            fontWeight: 'normal',
            color: 't.black70',
            mb: 'm',
          }}
        >
          *Required Fields
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <div className="form-group-field">
              <TextField
                data-testid="campaignDetailsFormCampaignDetailsUseCaseInput"
                id="brandId"
                inputProps={{ 'aria-label': 'brandId' }}
                disabled
                fullWidth
                label="Use-Case"
                value={useCase.id}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <div className="form-group-field">
              <StyledTextField
                data-testid="campaignDetailsFormCampaignDetailsDescriptionInput"
                id="description"
                inputProps={{
                  'aria-label': 'description',
                  maxLength: 4096,
                  minLength: 40,
                }}
                required
                fullWidth
                multiline
                disabled={descriptionDisabled}
                rows={2}
                rowsMax={3}
                label="Campaign Description"
                helperText={charCounter({
                  value: description,
                  maxLength: 4096,
                })}
                value={description}
                error={!!descriptionError?.message}
                onChange={(event) => setDescription(event.target.value)}
                onBlur={validateDescription}
              />
              {descriptionError?.message && (
                <h6 className="error-msg">{descriptionError.message}</h6>
              )}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="form-group-field">
              <StyledTextField
                data-testid="campaignDetailsFormCampaignDetailMessageFlowInput"
                id="messageFlow"
                inputProps={{
                  'aria-label': 'message flow',
                  maxLength: 4096,
                  minLength: 40,
                }}
                required
                fullWidth
                multiline
                disabled={messageFlowDisabled}
                rows={2}
                rowsMax={3}
                label="Call-to-Action / Message Flow"
                helperText={charCounter({
                  value: messageFlow,
                  maxLength: 4096,
                })}
                value={messageFlow}
                error={!!messageFlowError?.message}
                onChange={(event) => setMessageFlow(event.target.value)}
                onBlur={validateMessageFlow}
              />
              <Tooltip
                title="This field should describe how a consumer opts-in to the campaign, therefore giving consent to the sender to receive their messages. The call-to-action must be explicitly clear and inform the consumer of the nature of the program. If multiple opt-in methods can be used for the same campaign, you must list them all."
                style={{ top: '25px' }}
                placement="right-end"
              />
              {messageFlowError?.message && (
                <h6 className="error-msg">{messageFlowError.message}</h6>
              )}
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <div className="form-group-field">
              <StyledTextField
                data-testid="campaignDetailsFormCampaignDetailTermsAndConditionsLinkInput"
                id="termsAndConditionsLink"
                inputProps={{
                  'aria-label': 'terms and conditions link',
                  maxLength: 2048,
                }}
                fullWidth
                disabled={termsAndConditionsLinkDisabled}
                label="Terms and Conditions Link"
                helperText={charCounter({
                  value: termsAndConditionsLink,
                  maxLength: 2048,
                })}
                value={termsAndConditionsLink}
                error={!!termsAndConditionsLinkError?.message}
                onChange={(event) =>
                  setTermsAndConditionsLink(event.target.value)
                }
                onBlur={validateTermsAndConditionsLink}
              />
              {termsAndConditionsLinkError?.message && (
                <h6 className="error-msg">
                  {termsAndConditionsLinkError.message}
                </h6>
              )}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="form-group-field">
              <StyledTextField
                data-testid="campaignDetailsFormCampaignDetailsPrivacyPolicyLinkInput"
                id="privacyPolicyLink"
                inputProps={{
                  'aria-label': 'privacyPolicyLink',
                  maxLength: 2048,
                }}
                fullWidth
                disabled={privacyPolicyLinkDisabled}
                label="Privacy Policy Link"
                helperText={charCounter({
                  value: privacyPolicyLink,
                  maxLength: 2048,
                })}
                value={privacyPolicyLink}
                error={!!privacyPolicyLinkError?.message}
                onChange={(event) => setPrivacyPolicyLink(event.target.value)}
                onBlur={validatePrivacyPolicyLink}
              />
              {privacyPolicyLinkError?.message && (
                <h6 className="error-msg">{privacyPolicyLinkError.message}</h6>
              )}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div className="form-group-field">
              <StyledTextField
                data-testid="campaignDetailsFormCampaignDetailsReferenceId"
                id="referenceId"
                inputProps={{
                  'aria-label': 'referenceId',
                  maxLength: 50,
                }}
                fullWidth
                disabled={referenceIdDisabled}
                label="Campaign Reference ID"
                helperText={charCounter({
                  value: referenceId,
                  maxLength: 50,
                })}
                value={referenceId}
                error={!!referenceIdError?.message}
                onChange={(event) => setReferenceId(event.target.value)}
                onBlur={validateReferenceId}
              />
              {referenceIdError?.message && (
                <h6 className="error-msg">{referenceIdError.message}</h6>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};

export default CampaignDetailsSection;
